/**
 * Monitor action executors for chat actions
 */

import { ChatAction, ChatMessage as ChatMessageType } from '../../types/chat';
import { apiClient } from '../../lib/api';
import { ActionExecutorContext } from './actionExecutors';
import type { MonitorCreateRequest, MonitorWithDatasetRequest, MonitorUpdateRequest } from '../../types/monitoring';
import { dispatchMonitorEvent } from '../../utils/monitorEvents';
import { formatDateTime as formatDateTimeUtil } from '../../utils/dateUtils';
import { MAX_RESULTS_LIMIT } from '../../constants/monitorSignals';

/**
 * Validate that a string looks like a UUID
 */
function isValidUUID(str: string): boolean {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return uuidRegex.test(str);
}

/**
 * Validate monitor_id parameter and throw descriptive error if invalid
 */
function validateMonitorId(monitorId: string | undefined): string {
  if (!monitorId) {
    throw new Error('Monitor ID is required. The AI should have provided the monitor_id from the dataset context.');
  }
  if (!isValidUUID(monitorId)) {
    throw new Error(
      `Invalid monitor ID format: "${monitorId}". ` +
      'The monitor_id should be a UUID (e.g., "abc123-def456-..."). ' +
      'Please try again and ask to see your monitors first.'
    );
  }
  return monitorId;
}

/**
 * Format a cron expression into a human-readable schedule string
 */
function formatScheduleFromCron(cron: string): string {
  const parts = cron.split(' ');
  if (parts.length !== 5) return cron;

  const [, hour, dayOfMonth, , dayOfWeek] = parts;
  const hourNum = parseInt(hour, 10);

  // Handle non-numeric hours (e.g., "*", "*/2") - return raw cron if can't parse
  if (isNaN(hourNum)) return cron;

  const hour12 = hourNum === 0 ? 12 : hourNum > 12 ? hourNum - 12 : hourNum;
  const period = hourNum < 12 ? 'AM' : 'PM';

  if (dayOfMonth === '1' && dayOfWeek === '*') {
    return `Monthly on the 1st at ${hour12}:00 ${period}`;
  }
  if (dayOfWeek !== '*') {
    const dayNames: Record<string, string> = {
      '0': 'Sunday', '1': 'Monday', '2': 'Tuesday', '3': 'Wednesday',
      '4': 'Thursday', '5': 'Friday', '6': 'Saturday'
    };
    const days = dayOfWeek.split(',').map(d => dayNames[d] || d).join(', ');
    return `Weekly on ${days} at ${hour12}:00 ${period}`;
  }
  return `Daily at ${hour12}:00 ${period}`;
}

/**
 * Format monitor status with indicator
 */
function formatStatus(status: string): string {
  const statusMap: Record<string, string> = {
    'enabled': '● Active',
    'disabled': '○ Paused',
    'error': '⚠ Error'
  };
  return statusMap[status] || status;
}

/**
 * Format a date string for display, with 'Never' fallback for undefined
 */
function formatDateTime(dateStr: string | undefined): string {
  if (!dateStr) return 'Never';
  return formatDateTimeUtil(dateStr);
}

export const executeCreateMonitor = async (
  action: ChatAction,
  context: ActionExecutorContext
): Promise<void> => {
  try {
    if (!context.tableId) {
      throw new Error('No dataset selected. Please open a dataset first.');
    }

    const requestedMaxResults = action.parameters?.max_results || 25;
    const cappedMaxResults = Math.min(requestedMaxResults, MAX_RESULTS_LIMIT);
    const wasLimitExceeded = requestedMaxResults > MAX_RESULTS_LIMIT;

    const request: MonitorCreateRequest = {
      lead_table_id: context.tableId,
      name: action.parameters?.name || 'New Monitor',
      cron_expression: action.parameters?.cron_expression || '0 8 * * *',
      timezone: action.parameters?.timezone || 'Etc/UTC',
      max_results: cappedMaxResults,
      search_behavior: action.parameters?.search_behavior || 'append',
      description: action.parameters?.description
    };

    const monitor = await apiClient.createMonitor(request);

    // Add note if we capped the max results
    const limitNote = wasLimitExceeded
      ? `\n\n*Note: I can deliver a maximum of ${MAX_RESULTS_LIMIT} results per run, so I've set it to ${MAX_RESULTS_LIMIT} instead of ${requestedMaxResults}.*`
      : '';

    // Notify other components about the new monitor
    dispatchMonitorEvent('monitor:created', { monitorId: monitor.id, tableId: context.tableId });

    const successMessage: ChatMessageType = {
      id: (Date.now() + 1).toString(),
      role: 'assistant',
      content: `## Monitor Created ✓\n\n` +
        `**${monitor.name}**\n\n` +
        `**Status:** ${formatStatus(monitor.status)}\n` +
        `**Schedule:** ${formatScheduleFromCron(monitor.cron_expression)}\n` +
        `**Max Results:** ${monitor.max_results} per run\n\n` +
        `Your monitor is now active and will automatically find new results matching your dataset criteria.${limitNote}`,
      timestamp: new Date()
    };
    context.setMessages(prev => [...prev, successMessage]);
  } catch (error) {
    const errorMessage: ChatMessageType = {
      id: (Date.now() + 1).toString(),
      role: 'assistant',
      content: `Failed to create monitor: ${error instanceof Error ? error.message : 'Unknown error'}`,
      timestamp: new Date()
    };
    context.setMessages(prev => [...prev, errorMessage]);
  }
};

export const executeCreateMonitorWithDataset = async (
  action: ChatAction,
  context: ActionExecutorContext
): Promise<void> => {
  try {
    const params = action.parameters || {};

    if (!params.event_type || !params.entity_type || !params.criteria || !params.cron_expression) {
      throw new Error('Missing required parameters: event_type, entity_type, criteria, and cron_expression are required.');
    }

    const requestedMaxResults = params.max_results || 10;
    const cappedMaxResults = Math.min(requestedMaxResults, MAX_RESULTS_LIMIT);
    const wasLimitExceeded = requestedMaxResults > MAX_RESULTS_LIMIT;

    const request: MonitorWithDatasetRequest = {
      event_type: params.event_type,
      entity_type: params.entity_type,
      criteria: params.criteria,
      cron_expression: params.cron_expression,
      timezone: params.timezone || 'Etc/UTC',
      max_results: cappedMaxResults,
      custom_event_text: params.custom_event_text,
      custom_entity_description: params.custom_entity_description,
      search_behavior: params.search_behavior || 'append',
      name: params.name,
      description: params.description
    };

    const result = await apiClient.createMonitorWithDataset(request);

    // Notify other components about the new monitor
    dispatchMonitorEvent('monitor:created', { monitorId: result.monitor.id, tableId: result.dataset.id });

    // Add note if we capped the max results
    const limitNote = wasLimitExceeded
      ? `\n\n*Note: I can deliver a maximum of ${MAX_RESULTS_LIMIT} results per run, so I've set it to ${MAX_RESULTS_LIMIT} instead of ${requestedMaxResults}.*`
      : '';

    const successMessage: ChatMessageType = {
      id: (Date.now() + 1).toString(),
      role: 'assistant',
      content: `## Monitor & Dataset Created ✓\n\n` +
        `**Monitor:** ${result.monitor.name}\n` +
        `**Dataset:** ${result.dataset.name}\n\n` +
        `**Status:** ${formatStatus(result.monitor.status)}\n` +
        `**Schedule:** ${formatScheduleFromCron(result.monitor.cron_expression)}\n` +
        `**Max Results:** ${result.monitor.max_results} per run\n\n` +
        `The dataset is now generating. Once complete, the monitor will automatically run on schedule to find new matching results.${limitNote}`,
      timestamp: new Date()
    };
    context.setMessages(prev => [...prev, successMessage]);

    // Trigger navigation to the new dataset if callback available
    if (context.onActionTriggered) {
      context.onActionTriggered({
        type: 'navigate_to_dataset',
        payload: { datasetId: result.dataset.id }
      });
    }
  } catch (error) {
    const errorMessage: ChatMessageType = {
      id: (Date.now() + 1).toString(),
      role: 'assistant',
      content: `Failed to create monitor with dataset: ${error instanceof Error ? error.message : 'Unknown error'}`,
      timestamp: new Date()
    };
    context.setMessages(prev => [...prev, errorMessage]);
  }
};

export const executeListMonitors = async (
  action: ChatAction,
  context: ActionExecutorContext
): Promise<void> => {
  try {
    const tableId = action.parameters?.table_id || context.tableId;
    const result = await apiClient.listMonitors(tableId);

    if (result.monitors.length === 0) {
      const message: ChatMessageType = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: "You don't have any monitors set up yet. Would you like to create one? You can set up a monitor on your current dataset or create a new one to track specific signals like funding rounds, job changes, or product launches.",
        timestamp: new Date()
      };
      context.setMessages(prev => [...prev, message]);
    } else {
      const monitorCards = result.monitors.map(m => {
        const status = formatStatus(m.status);
        const schedule = formatScheduleFromCron(m.cron_expression);
        const lastRun = formatDateTime(m.last_run_at);
        const nextRun = m.status === 'enabled' ? formatDateTime(m.next_run_at) : 'Paused';
        const dataset = m.lead_table_name || 'Unknown dataset';

        return `### ${m.name}\n` +
          `**Status:** ${status}\n` +
          `**Schedule:** ${schedule}\n` +
          `**Dataset:** ${dataset}\n` +
          `**Max Results:** ${m.max_results} per run\n` +
          `**Last Run:** ${lastRun}\n` +
          `**Next Run:** ${nextRun}`;
      }).join('\n\n---\n\n');

      const message: ChatMessageType = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: `## Your Monitors (${result.count})\n\n${monitorCards}`,
        timestamp: new Date()
      };
      context.setMessages(prev => [...prev, message]);
    }
  } catch (error) {
    const errorMessage: ChatMessageType = {
      id: (Date.now() + 1).toString(),
      role: 'assistant',
      content: `Failed to list monitors: ${error instanceof Error ? error.message : 'Unknown error'}`,
      timestamp: new Date()
    };
    context.setMessages(prev => [...prev, errorMessage]);
  }
};

export const executeUpdateMonitor = async (
  action: ChatAction,
  context: ActionExecutorContext
): Promise<void> => {
  try {
    const monitorId = validateMonitorId(action.parameters?.monitor_id);

    const updateRequest: MonitorUpdateRequest = {};
    if (action.parameters?.name !== undefined) updateRequest.name = action.parameters.name;
    if (action.parameters?.description !== undefined) updateRequest.description = action.parameters.description;
    if (action.parameters?.status !== undefined) {
      // Normalize status values - AI might send variations like "disable", "paused", "active"
      const statusLower = String(action.parameters.status).toLowerCase();
      if (statusLower === 'disabled' || statusLower === 'disable' || statusLower === 'paused' || statusLower === 'pause') {
        updateRequest.status = 'disabled';
      } else if (statusLower === 'enabled' || statusLower === 'enable' || statusLower === 'active' || statusLower === 'resume') {
        updateRequest.status = 'enabled';
      } else {
        updateRequest.status = action.parameters.status; // Pass through as-is, let backend validate
      }
    }
    if (action.parameters?.cron_expression !== undefined) updateRequest.cron_expression = action.parameters.cron_expression;
    if (action.parameters?.timezone !== undefined) updateRequest.timezone = action.parameters.timezone;
    let wasMaxResultsLimitExceeded = false;
    let requestedMaxResults = 0;
    if (action.parameters?.max_results !== undefined) {
      requestedMaxResults = action.parameters.max_results;
      wasMaxResultsLimitExceeded = requestedMaxResults > MAX_RESULTS_LIMIT;
      updateRequest.max_results = Math.min(requestedMaxResults, MAX_RESULTS_LIMIT);
    }
    if (action.parameters?.search_behavior !== undefined) updateRequest.search_behavior = action.parameters.search_behavior;

    const monitor = await apiClient.updateMonitor(monitorId, updateRequest);

    // Notify other components about the monitor update
    dispatchMonitorEvent('monitor:updated', { monitorId: monitor.id, tableId: context.tableId });

    let actionTaken = 'Settings updated';
    if (action.parameters?.status === 'disabled') {
      actionTaken = 'Monitor paused';
    } else if (action.parameters?.status === 'enabled') {
      actionTaken = 'Monitor resumed';
    } else if (action.parameters?.cron_expression) {
      actionTaken = 'Schedule updated';
    } else if (action.parameters?.name) {
      actionTaken = 'Monitor renamed';
    }

    // Add note if we capped the max results
    const maxResultsNote = wasMaxResultsLimitExceeded
      ? `\n\n*Note: I can deliver a maximum of ${MAX_RESULTS_LIMIT} results per run, so I've set it to ${MAX_RESULTS_LIMIT} instead of ${requestedMaxResults}.*`
      : '';

    const successMessage: ChatMessageType = {
      id: (Date.now() + 1).toString(),
      role: 'assistant',
      content: `## ${actionTaken} ✓\n\n` +
        `**${monitor.name}**\n\n` +
        `**Status:** ${formatStatus(monitor.status)}\n` +
        `**Schedule:** ${formatScheduleFromCron(monitor.cron_expression)}\n` +
        `**Max Results:** ${monitor.max_results} per run${maxResultsNote}`,
      timestamp: new Date()
    };
    context.setMessages(prev => [...prev, successMessage]);
  } catch (error) {
    const errorMessage: ChatMessageType = {
      id: (Date.now() + 1).toString(),
      role: 'assistant',
      content: `Failed to update monitor: ${error instanceof Error ? error.message : 'Unknown error'}`,
      timestamp: new Date()
    };
    context.setMessages(prev => [...prev, errorMessage]);
  }
};

export const executeDeleteMonitor = async (
  action: ChatAction,
  context: ActionExecutorContext
): Promise<void> => {
  try {
    const monitorId = validateMonitorId(action.parameters?.monitor_id);

    await apiClient.deleteMonitor(monitorId);

    // Notify other components about the deletion
    dispatchMonitorEvent('monitor:deleted', { monitorId, tableId: context.tableId });

    const successMessage: ChatMessageType = {
      id: (Date.now() + 1).toString(),
      role: 'assistant',
      content: `## Monitor Deleted ✓\n\nThe monitor has been permanently removed and will no longer run.`,
      timestamp: new Date()
    };
    context.setMessages(prev => [...prev, successMessage]);
  } catch (error) {
    const errorMessage: ChatMessageType = {
      id: (Date.now() + 1).toString(),
      role: 'assistant',
      content: `Failed to delete monitor: ${error instanceof Error ? error.message : 'Unknown error'}`,
      timestamp: new Date()
    };
    context.setMessages(prev => [...prev, errorMessage]);
  }
};

export const executeListMonitorRuns = async (
  action: ChatAction,
  context: ActionExecutorContext
): Promise<void> => {
  try {
    const monitorId = validateMonitorId(action.parameters?.monitor_id);

    const limit = action.parameters?.limit || 10;
    const result = await apiClient.listMonitorRuns(monitorId, limit);

    if (result.runs.length === 0) {
      const message: ChatMessageType = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: "This monitor hasn't run yet. It will run according to its schedule.",
        timestamp: new Date()
      };
      context.setMessages(prev => [...prev, message]);
    } else {
      const runsList = result.runs.slice(0, limit).map(r => {
        const date = formatDateTime(r.started_at);
        const statusIndicator: Record<string, string> = {
          'completed': '✓ Completed',
          'running': '◐ Running',
          'failed': '✕ Failed',
          'canceled': '○ Canceled',
          'pending': '◔ Pending'
        };
        const status = statusIndicator[r.status] || r.status;
        const errorInfo = r.error_message ? `\n   Error: ${r.error_message}` : '';

        return `**${date}**\n` +
          `   ${status} — Found: ${r.items_found}, Added: ${r.items_added}${errorInfo}`;
      }).join('\n\n');

      const message: ChatMessageType = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: `## Run History (${result.count} total)\n\n${runsList}`,
        timestamp: new Date()
      };
      context.setMessages(prev => [...prev, message]);
    }
  } catch (error) {
    const errorMessage: ChatMessageType = {
      id: (Date.now() + 1).toString(),
      role: 'assistant',
      content: `Failed to get monitor runs: ${error instanceof Error ? error.message : 'Unknown error'}`,
      timestamp: new Date()
    };
    context.setMessages(prev => [...prev, errorMessage]);
  }
};
